import Input from '../../../Input/Input';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { array, object } from 'yup';
import { emailScheme, stringScheme } from 'src/utils/validation-schemes';
import { useParams } from 'react-router-dom';
import {
  useAddIntegrationMutation,
  useGetProjectByIdQuery,
  useLazyGetZendeskConsentLinkQuery,
  useUpdateIntegrationMutation,
} from 'src/services';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { IncludeTicketFields } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/IncludeTicketFields/IncludeTicketFields';
import * as yup from 'yup';
import { Button } from 'src/components/ui/button';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import ChevronDownDisabledIcon from 'src/assets/img/chevron-down-disabled-icon.svg';
import { clsx } from 'clsx';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Spinner } from 'src/components/Spinner/Spinner';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useIntegrationAuthorizationPolling } from 'src/components/Integrations/useIntegrationAuthorizationPolling';

interface ZendeskEmailFormValues {
  subdomain: string;
  email: string;
  include_custom_fields: boolean;
  api_key: string;
  // Added by IncludeTicketFields.
  custom_fields?: { key?: string; id: string; name: string }[];
}

export const IntegrationFormZendeskEmail = ({
  integration: integrationProp,
  onSubmit,
}: {
  // Editing an existing integration passes it in; the add flow reads it from the store.
  integration?: SelectedIntegration;
  onSubmit: IntegrationSubmitHandler;
}) => {
  const { id } = useParams();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const integration = integrationProp ?? selectedIntegration;
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const loader = useLoader();
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(
    Boolean(integration?.integration_data?.email),
  );
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isZendeskAuthorized =
    integration?.integration_data?.auth_type === 'oauth' &&
    integration?.integration_data?.auth_status === 'ok';
  const isManualConnectActive = isManualConnectOpen && !isZendeskAuthorized;
  const [addIntegration] = useAddIntegrationMutation();
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useIntegrationAuthorizationPolling(id);
  const { handleSubmit, control, watch, clearErrors } = useForm<ZendeskEmailFormValues>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain || '',
      email: integration?.integration_data?.email || '',
      include_custom_fields: false,
      api_key: '',
    },
    resolver: formResolver(
      yup.lazy((values, { context }) => {
        const isManualConnect = Boolean(context?.isManualConnectOpen);
        return object({
          subdomain: stringScheme({ required: true }).label('Zendesk subdomain'),
          api_key: stringScheme({ required: isManualConnect }).label('API token'),
          email: emailScheme(isManualConnect ? { required: true } : { allowEmpty: true }).label(
            'Admin email address',
          ),
          custom_fields: array(
            object({
              id: stringScheme({ required: values.include_custom_fields }),
              name: stringScheme({ required: values.include_custom_fields }),
            }),
          ),
        });
      }),
    ),
    // API token credentials are only required when connecting manually.
    context: { isManualConnectOpen: isManualConnectActive },
  });

  const onConnectWithZendesk = async (data: ZendeskEmailFormValues) => {
    let selectedIntegration = { ...integration };

    loader.show();

    const request = {
      subdomain: data.subdomain,
      include_custom_fields: data.include_custom_fields,
      custom_fields: data.include_custom_fields
        ? data.custom_fields.map(({ id, name }) => {
            return { id, name };
          })
        : null,
    };
    if (!integration.id) {
      const { data: addIntegrationData, error } = await addIntegration({
        id,
        body: {
          integration_type: integration.data.integration_type,
          integration_data: request,
        },
      });

      if (!error) {
        selectedIntegration = { ...selectedIntegration, ...addIntegrationData };
        notification.success(
          `Integration ${integration?.friendlyName || addIntegrationData.integration_type} was added.`,
        );
        dispatch(setSelectedIntegration(selectedIntegration));
      }
    } else {
      const { data: updatedIntegration, error } = await updateIntegration({
        projectId: id,
        integrationId: integration.id,
        body: {
          integration_type: integration.data.integration_type,
          integration_data: request,
        },
      });
      if (!error) {
        selectedIntegration = { ...selectedIntegration, ...updatedIntegration };
        notification.success(
          `Integration ${integration?.friendlyName || updatedIntegration.integration_type} was updated.`,
        );
        dispatch(setSelectedIntegration(selectedIntegration));
      }
    }

    if (
      selectedIntegration?.id &&
      selectedIntegration?.integration_data?.auth_type === 'oauth' &&
      selectedIntegration?.integration_data?.auth_status === 'needs_reauthorization'
    ) {
      const { data: consentLink } = await getZendeskConsentLinkQuery({
        project_id: id,
        integration_id: selectedIntegration.id,
        type: 'zendesk-email',
      });

      if (consentLink?.authorize_url) {
        window.open(consentLink.authorize_url);
        pollZendeskAuthorization(selectedIntegration.id, (authorizedIntegration) =>
          dispatch(setSelectedIntegration({ ...integration, ...authorizedIntegration })),
        );
      }
    }

    loader.hide();
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit((data) => onConnectWithZendesk(data));

  const submit = async (data: ZendeskEmailFormValues) => {
    const isNotCreated = !integration?.id;
    const isNotAuthorized =
      integration?.integration_data?.auth_type === 'oauth' &&
      integration?.integration_data?.auth_status !== 'ok';

    // Manual API token credentials are submitted as-is; only the OAuth route runs the connect flow.
    if (!isManualConnectActive && (isNotCreated || isNotAuthorized)) {
      return await onConnectWithZendesk(data);
    }
    const skip = Boolean(integration.id);
    const body: { integration_type: string; integration_data: Record<string, unknown> } = {
      integration_type: integration.data.integration_type,
      integration_data: {
        ...integration.integration_data,
        project_name: currentProject.name,
        subdomain: data.subdomain,
        include_custom_fields: data.include_custom_fields,
      },
    };

    if (data.email) {
      body.integration_data.email = data.email;
    } else {
      delete body.integration_data.email;
    }

    if (data.api_key) {
      body.integration_data.api_key = data.api_key;
    } else {
      delete body.integration_data.api_key;
    }

    if (data.include_custom_fields) {
      body.integration_data.custom_fields = data.custom_fields.map((field) => {
        return { id: field.id, name: `${field.name}` };
      });
    } else {
      body.integration_data.custom_fields = null;
    }

    if (integration.id) {
      const { data: updatedIntegration } = await updateIntegration({
        projectId: id,
        integrationId: integration.id,
        body,
      });

      dispatch(setSelectedIntegration({ ...integration, ...updatedIntegration }));
    }

    onSubmit(body, skip);
  };

  return (
    <form
      id="integration-form"
      className={'flex flex-col gap-2.5 overflow-auto max-h-[60vh] pr-4'}
      onSubmit={handleSubmit(submit)}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="content">
        <Controller
          control={control}
          name="subdomain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Zendesk subdomain'}
              placeholder={'domain.com'}
              invalidMessage={error?.message}
              readonly={isZendeskAuthorized}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain) {
                  onChange(res?.subdomain);
                } else {
                  onChange(e.target.value);
                }
              }}
              onFocus={() => {
                clearErrors('subdomain');
              }}
              infoTooltip={{
                side: 'right',
                description: 'Link to your Zendesk workspace.',
              }}
            />
          )}
        />
      </div>
      <div className={'mt-5 flex flex-col gap-2'}>
        <Button
          type={'button'}
          size={'sm'}
          onClick={connectWithZendesk}
          disabled={isManualConnectActive || isZendeskAuthorized}
          className={clsx(
            'w-full gap-2 px-8',
            isZendeskAuthorized
              ? 'bg-purple_150 text-purple_600 font-medium shadow-none disabled:opacity-100'
              : 'font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]',
          )}
        >
          {isZendeskAuthorized ? (
            'Connected successfully with Zendesk'
          ) : (
            <>
              {'Connect with Zendesk'}
              <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
            </>
          )}
        </Button>
        <p className={'text-center text-[13px] font-medium text-tertiary'}>
          You’ll be redirected to Zendesk to authorize access.
        </p>
        <div className={'flex items-center gap-2'}>
          <Separator className={'flex-1 bg-border_subtle'} />
          <span className={'text-[13px] font-medium text-tertiary'}>or</span>
          <Separator className={'flex-1 bg-border_subtle'} />
        </div>
        <Collapsible
          open={isManualConnectActive}
          onOpenChange={setIsManualConnectOpen}
          disabled={isZendeskAuthorized}
          triggerClassName={
            'group flex w-full items-center gap-2 rounded-lg border border-grey_border bg-white px-3 py-2 text-sm font-medium text-grey_750 transition-colors enabled:hover:border-purple_600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple_600/20 data-[state=open]:border-purple_600 disabled:cursor-not-allowed disabled:border-border_subtle disabled:text-grey_300'
          }
          trigger={
            <>
              <span className={'flex-1 text-center'}>Connect manually with API token</span>
              {isZendeskAuthorized ? (
                <img src={ChevronDownDisabledIcon} alt={''} width={16} height={16} />
              ) : (
                <img
                  src={ChevronDownIcon}
                  alt={''}
                  width={12.8}
                  height={12.8}
                  className={'transition-transform duration-200 group-data-[state=open]:rotate-180'}
                />
              )}
            </>
          }
          contentClassName={
            'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'
          }
        >
          <div className={'flex flex-col gap-2.5 pt-2'}>
            <div className="content">
              <Controller
                control={control}
                name="api_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'API token'}
                    placeholder={'Yb2KQfS8VQj1HxZlOzVbXk4P6W1Cm3aA1dFxGnYq'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <p>
                          Check how to find the API Key{' '}
                          <a
                            className={'underline'}
                            href={ZENDESK_HELP_CENTER_URL.API_TOKEN}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            here →
                          </a>
                        </p>
                      ),
                    }}
                  />
                )}
              />
            </div>
            <div className="content">
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Admin email address'}
                    placeholder={'email@domain.com'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: 'Email address of Zendesk user used to create API Token',
                    }}
                  />
                )}
              />
            </div>
          </div>
        </Collapsible>
      </div>
      <div className={'mt-5'}>
        <IncludeTicketFields
          control={control}
          watch={watch}
          clearErrors={clearErrors}
          inputFieldType={'number'}
        />
      </div>
    </form>
  );
};
